import React from 'react'
import { Package, ChevronRight } from 'lucide-react'
import { useLanguage } from '../../contexts/LanguageContext'
import type { ProductNameGroup } from '../../utils/productGrouping'
import { StockBadge } from './ui/stock-badge'

export type ProductGroupDensity = 'grid' | 'list'

interface ProductGroupCardProps {
  group: ProductNameGroup
  density: ProductGroupDensity
  showPricing: boolean
  onSelect: (group: ProductNameGroup) => void
  colorSwatches?: Array<{ id: string; name: string; hex_code?: string }>
}

export function ProductGroupCard({
  group,
  density,
  showPricing,
  onSelect,
  colorSwatches = []
}: ProductGroupCardProps) {
  const { t } = useLanguage()
  const previewColors = group.colors.slice(0, 5)
  const overflowCount = Math.max(0, group.colors.length - 5)
  const isOutOfStock = group.totalQuantity <= 0

  const swatchElements = previewColors.map(color => {
    const meta = colorSwatches.find(c => c.id === color.colorId)
    const hex = color.hexCode || meta?.hex_code || '#ccc'
    return (
      <span
        key={color.colorId}
        className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 shadow-sm flex-shrink-0"
        style={{ backgroundColor: hex }}
        title={color.colorName}
      />
    )
  })

  if (density === 'list') {
    return (
      <button
        type="button"
        onClick={() => onSelect(group)}
        className="w-full flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-teal-400 dark:hover:border-teal-600 hover:shadow-md transition-all text-left group"
      >
        <div className="h-14 w-14 rounded-lg bg-gray-100 dark:bg-gray-900 overflow-hidden flex-shrink-0">
          {group.imageUrl ? (
            <img src={group.imageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <Package className="h-6 w-6 text-gray-400" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
            {group.productName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {group.variantCount} variants
            {group.category ? ` · ${group.category}` : ''}
          </p>
          <div className="flex items-center gap-1.5 mt-2">
            {swatchElements}
            {overflowCount > 0 && (
              <span className="text-xs text-gray-400">+{overflowCount}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {isOutOfStock ? (
            <StockBadge quantity={0} showLabel size="sm" />
          ) : (
            <StockBadge quantity={group.totalQuantity} size="sm" />
          )}
          {showPricing && group.sellPrice != null && group.sellPrice > 0 && (
            <span className="text-sm font-bold text-green-600 dark:text-green-400">
              {'\u20AC'}{group.sellPrice.toFixed(2)}
            </span>
          )}
          <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-teal-500" />
        </div>
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(group)}
      className="group relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:border-teal-400 dark:hover:border-teal-600 hover:shadow-lg transition-all text-left flex flex-col w-full"
    >
      <div className="aspect-square bg-gray-100 dark:bg-gray-900 relative overflow-hidden">
        {group.imageUrl ? (
          <img
            src={group.imageUrl}
            alt={group.productName}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-12 w-12 text-gray-400" />
          </div>
        )}
        <div className="absolute top-2 right-2">
          {isOutOfStock ? (
            <StockBadge quantity={0} showLabel size="sm" />
          ) : (
            <StockBadge quantity={group.totalQuantity} size="sm" />
          )}
        </div>
      </div>

      <div className="p-3 flex flex-col flex-grow">
        <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 line-clamp-2 min-h-[2.5rem]">
          {group.productName}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {group.variantCount} variants
          {group.category ? ` · ${group.category}` : ''}
        </p>
        <div className="flex items-center gap-1 mt-2 flex-wrap">
          {swatchElements}
          {overflowCount > 0 && (
            <span className="text-xs text-gray-400">+{overflowCount}</span>
          )}
        </div>
        <div className="mt-auto pt-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-700">
          {showPricing && group.sellPrice != null && group.sellPrice > 0 ? (
            <span className="text-lg font-bold text-green-600 dark:text-green-400">
              {'\u20AC'}{group.sellPrice.toFixed(2)}
            </span>
          ) : (
            <span className="text-xs text-gray-400">{t('inhouse.viewVariants')}</span>
          )}
          <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-teal-500" />
        </div>
      </div>
    </button>
  )
}
